import * as React from "react";
import { useLocation, useNavigate } from "react-router";
import { ArrowRight, NotebookPen, Plus, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Body, BodySm, Caption } from "@/components/ui/typography";
import { modeFromPath, navFor, type NavItem } from "@/components/layout/nav";
import { cn } from "@/lib/utils";

interface Command {
  id: string;
  label: string;
  hint: string;
  to: string;
  icon: NavItem["icon"];
}

/** The track in the URL, so the palette jumps inside the track you are already looking at. */
function trackFromPath(pathname: string): string | undefined {
  return /^\/t\/([^/]+)/.exec(pathname)?.[1];
}

function buildCommands(pathname: string): Command[] {
  const trackId = trackFromPath(pathname);
  const screens: Command[] = navFor(modeFromPath(pathname), trackId).map((item) => ({
    id: item.to,
    label: item.label,
    hint: "Screen",
    to: item.to,
    icon: item.icon,
  }));

  if (!trackId) return screens;

  return [
    ...screens,
    {
      id: "log-session",
      label: "Log a session",
      hint: "About eight seconds",
      to: `/t/${trackId}/log/new`,
      icon: Plus,
    },
    {
      id: "log-week",
      label: "Log a whole week",
      hint: "One pass, seven days",
      to: `/t/${trackId}/log/new?span=week`,
      icon: NotebookPen,
    },
    {
      id: "create-period",
      label: "Create period",
      hint: "Collect entries to send",
      to: `/t/${trackId}/periods?new=1`,
      icon: Plus,
    },
  ];
}

/**
 * Cmd/Ctrl+K jumps to a screen or starts an entry.
 *
 * ponytail: plain `includes` matching, no cmdk and no fuzzy scorer. There are about eight
 * commands. A ranking algorithm over eight rows is code nobody can justify at 3am; swap in a
 * scorer if the list ever passes ~30.
 */
export function CommandPalette() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inputId = React.useId();
  const listId = React.useId();

  React.useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((wasOpen) => !wasOpen);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const commands = React.useMemo(() => buildCommands(pathname), [pathname]);
  const needle = query.trim().toLowerCase();
  const results = React.useMemo(
    () => (needle ? commands.filter((c) => c.label.toLowerCase().includes(needle)) : commands),
    [commands, needle],
  );

  const go = (command: Command | undefined) => {
    if (!command) return;
    setOpen(false);
    setQuery("");
    void navigate(command.to);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (results.length ? (index + 1) % results.length : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (results.length ? (index - 1 + results.length) % results.length : 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(results[active]);
    }
  };

  const activeId = results[active] ? `${listId}-${results[active].id}` : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <DialogContent showCloseButton={false} className="gap-3 p-4 sm:max-w-[34rem]">
        <DialogTitle className="sr-only">Jump to</DialogTitle>
        <DialogDescription className="sr-only">
          Type to find a screen, then press Enter.
        </DialogDescription>

        <Label htmlFor={inputId} className="sr-only">
          Jump to
        </Label>
        <div className="flex items-center gap-2">
          <Search aria-hidden="true" className="text-muted-foreground size-[19px] shrink-0" />
          <Input
            id={inputId}
            role="combobox"
            autoComplete="off"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            placeholder="Jump to"
            value={query}
            onChange={(event) => {
              // A new search starts at the top of its own list.
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            className="border-0 shadow-none focus-visible:ring-0"
          />
        </div>

        {results.length === 0 ? (
          <BodySm className="text-muted-foreground px-2 py-6 text-center">
            Nothing here by that name. Try a shorter word.
          </BodySm>
        ) : (
          <ul id={listId} role="listbox" aria-label="Jump to" className="flex flex-col gap-1">
            {results.map((command, index) => {
              const Icon = command.icon;
              const selected = index === active;
              return (
                // The keyboard path is the combobox's own: arrows move, Enter runs. An option in
                // a listbox never takes focus itself, so it has no key handler of its own.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  key={command.id}
                  id={`${listId}-${command.id}`}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(command)}
                  className={cn(
                    "flex h-11 cursor-pointer items-center gap-3 rounded-[14px] px-3",
                    selected && "bg-accent",
                  )}
                >
                  <Icon aria-hidden="true" className="text-muted-foreground size-[17px] shrink-0" />
                  <Body className="flex-1 truncate">{command.label}</Body>
                  <Caption className="text-muted-foreground hidden sm:block">
                    {command.hint}
                  </Caption>
                  {selected ? (
                    <ArrowRight aria-hidden="true" className="text-muted-foreground size-[17px]" />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
